// `PDFX` (SuiteCompile/PDFX.swift) and `PDFXCheck` (SuitePreflight/PDFXCheck.swift): a print-ready Typst PDF (PDF 1.4,
// untagged: TypstKit's flag 1) made into PDF/X-1a:2001 for IngramSpark, and the check of the result. Typst can't write
// PDF/X, but its print interiors already meet most of it, so this adds what the standard requires (version 1.3, a
// GTS_PDFX output intent for the registered "CGATS TR 001" condition, the info keys, TrimBox/BleedBox) and removes what
// it forbids (page annotations and transparency groups, the structure tree).
import { sha256Hex } from '@writing-suite/core';
import {
  asArray, asDict, asName, asNumber, asText, PDFDict, PDFFailure, PDFFile, PDFLexer, V, type PDFObject, type PDFValue,
} from './pdfFile.ts';
import type { PreflightFinding } from '../preflight.ts';

export const PDFX_VERSION = 'PDF/X-1:2001';
export const PDFX_CONFORMANCE = 'PDF/X-1a:2001';

/** "D:20260927093000Z" */
export function pdfDate(date: Date): string {
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `D:${p(date.getUTCFullYear(), 4)}${p(date.getUTCMonth() + 1)}${p(date.getUTCDate())}${p(date.getUTCHours())}${p(date.getUTCMinutes())}${p(date.getUTCSeconds())}Z`;
}

/** `PDFX.convert`: the PDF/X-1a:2001 file. `bleed` in inches. Throws `PDFFailure` for a file it can't rewrite. */
export function convertToPDFX(data: Uint8Array, title: string, bleed: number, date = new Date()): Uint8Array {
  const file = new PDFFile(data);
  const root = file.trailer.get('Root');
  const catalog = root?.t === 'ref' ? asDict(file.object(root.n).value)?.clone() : null;
  if (root?.t !== 'ref' || !catalog) throw new PDFFailure('malformed', 'no document catalog');
  const replace = new Map<number, PDFValue>();
  const b = bleed * 72;
  file.pages().forEach((page, index) => {
    const dict = asDict(file.object(page.number).value)?.clone();
    if (!dict) return;
    const media = page.mediaBox;
    dict.set('MediaBox', V.array(media.map(V.real)));
    dict.set('Annots', null);
    dict.set('Group', null);
    if (!dict.has('TrimBox')) {
      // Bleed runs off the top, bottom and outside edge: the right of a recto (odd page), the left of a verso.
      const recto = index % 2 === 0;
      const trim = b > 0 ? [media[0] + (recto ? 0 : b), media[1] + b, media[2] - (recto ? b : 0), media[3] - b] : media;
      dict.set('TrimBox', V.array(trim.map(V.real)));
    }
    if (!dict.has('BleedBox')) dict.set('BleedBox', V.array(media.map(V.real)));
    replace.set(page.number, V.dict(dict));
  });

  // Info: keep what Typst wrote, add what PDF/X requires.
  const info = new PDFDict();
  const existing = asDict(file.resolve(file.trailer.get('Info')));
  if (existing) for (const key of ['Title', 'Author', 'Subject', 'Keywords', 'Creator', 'Producer', 'CreationDate']) info.set(key, existing.get(key));
  if ((asText(info.get('Title')) ?? '') === '') info.set('Title', V.text(title));
  const stamp = V.string(new TextEncoder().encode(pdfDate(date)), false);
  if (!info.has('CreationDate')) info.set('CreationDate', stamp);
  info.set('ModDate', stamp);
  info.set('Trapped', V.name('False'));
  info.set('GTS_PDFXVersion', V.text(PDFX_VERSION));
  info.set('GTS_PDFXConformance', V.text(PDFX_CONFORMANCE));

  const intent = new PDFDict([
    ['Type', V.name('OutputIntent')], ['S', V.name('GTS_PDFX')],
    ['OutputConditionIdentifier', V.text('CGATS TR 001')],
    ['OutputCondition', V.text('SWOP (Publication) Grade 5 Paper')],
    ['RegistryName', V.text('http://www.color.org')],
    ['Info', V.text('U.S. Web Coated (SWOP) v2')],
  ]);
  catalog.set('StructTreeRoot', null);
  catalog.set('MarkInfo', null);
  const firstNew = Math.max(0, ...file.offsets.keys()) + 1;
  catalog.set('OutputIntents', V.array([V.ref(firstNew, 0)]));
  replace.set(root.n, V.dict(catalog));

  let id = file.trailer.get('ID');
  if (!id) {
    const hash = sha256Hex(data).slice(0, 32);
    const bytes = new Uint8Array(16);
    for (let i = 0; i < 16; i++) bytes[i] = parseInt(hash.slice(i * 2, i * 2 + 2), 16);
    id = V.array([V.string(bytes, true), V.string(bytes, true)]);
  }
  const trailerID = id;
  return file.rewritten('1.3', replace, [V.dict(intent), V.dict(info)], (first) =>
    new PDFDict([['Root', V.ref(root.n, 0)], ['Info', V.ref(first + 1, 0)], ['ID', trailerID]]));
}

// MARK: PDFXCheck

/** Output conditions registered with the ICC, for which no profile need be embedded. */
const REGISTERED = new Set(['CGATS TR 001', 'CGATS TR 003', 'CGATS TR 005', 'CGATS TR 006', 'CGATS21_CRPC1', 'CGATS21_CRPC5',
  'CGATS21_CRPC6', 'FOGRA27', 'FOGRA39', 'FOGRA51', 'FOGRA52', 'JC200103']);

const tryResolve = (file: PDFFile, v: PDFValue | null | undefined) => { try { return file.resolve(v); } catch { return null; } };

/** `PDFXCheck.check`: what an Acrobat PDF/X-1a:2001 preflight profile would reject in a text interior. */
export function checkPDFX(data: Uint8Array): PreflightFinding[] {
  const f: PreflightFinding[] = [];
  const error = (code: string, message: string, page?: number) =>
    f.push({ severity: 'error', code: `pdfx.${code}`, message, ...(page !== undefined ? { page } : {}) });
  let file: PDFFile;
  try { file = new PDFFile(data); } catch (e) {
    return [{ severity: 'error', code: 'pdfx.structure', message: `The file isn't PDF/X-1a: ${(e as Error).message}` }];
  }
  if (file.version > '1.3') error('version', `The file is PDF ${file.version}; PDF/X-1a:2001 is PDF 1.3.`);
  if (file.trailer.has('Encrypt')) error('encrypted', 'The file is encrypted.');
  if (!file.trailer.has('ID')) error('id', 'The file has no document ID.');

  const info = asDict(tryResolve(file, file.trailer.get('Info')));
  if (!(asText(info?.get('GTS_PDFXVersion')) ?? '').startsWith('PDF/X-1')) error('version-key', "The document info doesn't say which PDF/X version it is.");
  if (!['True', 'False'].includes(asName(info?.get('Trapped')) ?? '')) error('trapped', "The document info doesn't say whether it's trapped.");
  for (const key of ['Title', 'CreationDate', 'ModDate']) if (!info?.has(key)) error('info', `The document info has no ${key}.`);

  const root = file.trailer.get('Root');
  let catalog: PDFDict | null = null;
  try { catalog = root?.t === 'ref' ? asDict(file.object(root.n).value) : null; } catch { catalog = null; }
  if (!catalog) return [...f, { severity: 'error', code: 'pdfx.structure', message: 'The file has no document catalog.' }];
  const intents = asArray(tryResolve(file, catalog.get('OutputIntents'))) ?? [];
  const pdfx = intents.map((i) => asDict(tryResolve(file, i))).find((d) => asName(d?.get('S')) === 'GTS_PDFX');
  if (pdfx) {
    const condition = asText(pdfx.get('OutputConditionIdentifier')) ?? '';
    if (condition === '') error('intent', 'The output intent names no printing condition.');
    else if (!pdfx.has('DestOutputProfile') && !REGISTERED.has(condition)) {
      error('intent', `The output intent's condition “${condition}” isn't registered, so it needs an embedded profile.`);
    }
  } else {
    error('intent', "There's no PDF/X output intent.");
  }

  // Pages: boxes, and nothing interactive or transparent on them.
  let pages: ReturnType<PDFFile['pages']> = [];
  try { pages = file.pages(); } catch (e) { error('pages', `The pages couldn't be read: ${(e as Error).message}`); }
  const numbers = (v: PDFValue | null) => (asArray(v)?.map(asNumber).filter((x): x is number => x !== null)) ?? null;
  pages.forEach((page, index) => {
    const dict = page.dict;
    const number = index + 1;
    const media = page.mediaBox;
    const trim = numbers(tryResolve(file, dict.get('TrimBox') ?? dict.get('ArtBox')));
    const bleed = numbers(tryResolve(file, dict.get('BleedBox'))) ?? media;
    if (!trim) error('trim', `Page ${number} has no trim box.`, number);
    if (!contains(media, bleed)) error('bleed', `Page ${number}'s bleed box is outside its media box.`, number);
    if (trim && !contains(bleed, trim)) error('trim', `Page ${number}'s trim box is outside its bleed box.`, number);
    const annotations = asArray(tryResolve(file, dict.get('Annots'))) ?? [];
    if (annotations.some((a) => asName(asDict(tryResolve(file, a))?.get('Subtype')) !== 'PrinterMark')) {
      error('annotations', `Page ${number} has links or other annotations.`, number);
    }
    if (asName(asDict(tryResolve(file, dict.get('Group')))?.get('S')) === 'Transparency') {
      error('transparency', `Page ${number} has a transparency group.`, number);
    }
  });

  // Every object: colour spaces, transparency and fonts.
  const colour = new Set<string>(), unembedded = new Set<string>();
  let transparency = false;
  for (const number of file.offsets.keys()) {
    let object: PDFObject;
    try { object = file.object(number); } catch { continue; }
    const dict = asDict(object.value);
    if (!dict) continue;
    for (const space of colourSpaces(object.value, file)) {
      if (!['DeviceGray', 'DeviceCMYK', 'Separation', 'DeviceN', 'Pattern', 'Indexed'].includes(space)) colour.add(space);
    }
    if (asName(dict.get('Type')) === 'ExtGState') {
      const smask = dict.get('SMask');
      if (smask && asName(smask) !== 'None') transparency = true;
      for (const key of ['CA', 'ca']) if ((asNumber(dict.get(key)) ?? 1) < 1) transparency = true;
      const mode = asName(dict.get('BM'));
      if (mode !== null && !['Normal', 'Compatible'].includes(mode)) transparency = true;
    }
    if (asName(dict.get('Subtype')) === 'Image' && dict.has('SMask')) transparency = true;
    if (asName(dict.get('Type')) === 'FontDescriptor' && !dict.has('FontFile') && !dict.has('FontFile2') && !dict.has('FontFile3')) {
      unembedded.add(asName(dict.get('FontName')) ?? 'a font');
    }
    // RGB set directly in page content (the rg/RG operators).
    if (!dict.has('Type') && !dict.has('Subtype') && object.stream) {
      const content = PDFFile.decode(object);
      if (content && content.length < 20_000_000 && containsRGBOperator(content)) colour.add('DeviceRGB');
    }
  }
  if (colour.size) {
    error('colour', `The file uses ${[...colour].sort().join(', ')} colour; PDF/X-1a allows only grey and CMYK (IngramSpark: black text at 100% K).`);
  }
  if (transparency) error('transparency', "The file uses transparency, which PDF/X-1a doesn't allow.");
  if (unembedded.size) error('fonts', `Not every font is embedded: ${[...unembedded].sort().slice(0, 3).join(', ')}.`);
  return f;
}

function contains(outer: number[], inner: number[]): boolean {
  if (outer.length !== 4 || inner.length !== 4) return true;
  const t = 0.01;
  return inner[0] >= outer[0] - t && inner[1] >= outer[1] - t && inner[2] <= outer[2] + t && inner[3] <= outer[3] + t;
}

/** The colour space families an object uses: its ColorSpace resources and an image's own ColorSpace. */
function colourSpaces(value: PDFValue, file: PDFFile): string[] {
  const dict = asDict(value);
  if (!dict) return [];
  const found: string[] = [];
  const family = (v: PDFValue | null | undefined) => {
    const r = tryResolve(file, v);
    if (!r) return;
    const name = asName(r);
    if (name !== null) { found.push(name); return; }
    const array = asArray(r);
    const first = array && array.length ? asName(array[0]) : null;
    if (array && first !== null) {
      found.push(first);
      if (first === 'Indexed' && array.length > 1) family(array[1]);
      if ((first === 'Separation' || first === 'DeviceN') && array.length > 2) family(array[array.length - 2]);
    }
  };
  if (asName(dict.get('Subtype')) === 'Image') family(dict.get('ColorSpace'));
  const spaces = asDict(tryResolve(file, dict.get('ColorSpace')));
  if (spaces) for (const [, space] of spaces.pairs) family(space);
  return found;
}

/** Whether content sets DeviceRGB colour ("r g b rg" or "RG"), outside strings. */
export function containsRGBOperator(bytes: Uint8Array): boolean {
  let i = 0, inString = 0;
  const r = 0x72, R = 0x52, g = 0x67, G = 0x47;
  while (i + 1 < bytes.length) {
    const c = bytes[i];
    if (inString > 0) {
      if (c === 0x5c) { i += 2; continue; }
      if (c === 0x28) inString++; else if (c === 0x29) inString--;
    } else if (c === 0x28) {
      inString = 1;
    } else if ((c === r || c === R) && bytes[i + 1] === (c === r ? g : G) && (i === 0 || PDFLexer.isWhite(bytes[i - 1]))
      && (i + 2 >= bytes.length || PDFLexer.isWhite(bytes[i + 2]) || PDFLexer.isDelimiter(bytes[i + 2]))) {
      return true;
    }
    i++;
  }
  return false;
}
